<template>
  <v-navigation-drawer
    :value="value"
    fixed
    temporary
    right
    :width="$vuetify.breakpoint.xsOnly ? '100%' : 460"
    class="expert-drawer"
    @input="$emit('input', $event)"
  >
    <div v-if="expert" class="expert-drawer__body">
      <!-- Шапка -->
      <div class="d-flex align-start">
        <v-avatar size="64" color="#483CB9" class="mr-4 flex-shrink-0">
          <img v-if="expert.photo_url" :src="expert.photo_url" :alt="name">
          <span v-else class="white--text text-h6">{{ initialsOf(name) }}</span>
        </v-avatar>
        <div class="flex-grow-1 min-width-0">
          <div class="expert-drawer__name">{{ name }}</div>
          <a v-if="expert.email" :href="`mailto:${expert.email}`" class="expert-drawer__email">{{ expert.email }}</a>
          <div class="mt-1">
            <v-chip x-small class="mr-1" :color="expert.role ? '#ece9fb' : '#fdecea'">{{ roleLabel }}</v-chip>
            <v-chip v-if="expert.is_active === false" x-small class="mr-1" color="#f0f0f0">Неактивен</v-chip>
            <v-chip v-else x-small class="mr-1" color="#e3f6ec" text-color="#009E60">Активен</v-chip>
            <v-chip v-if="isMe" x-small color="#483CB9" text-color="white">Это вы</v-chip>
          </div>
        </div>
        <v-btn icon small class="ml-2" @click="$emit('input', false)"><v-icon>mdi-close</v-icon></v-btn>
      </div>

      <v-btn small outlined color="#483CB9" class="text-none mt-4" @click="$emit('edit', expert)">
        <v-icon small left>mdi-pencil-outline</v-icon>
        Редактировать
      </v-btn>

      <!-- Цифры -->
      <div class="expert-drawer__stats">
        <button type="button" class="expert-stat" @click="$emit('show-appointments', { expertId: expert.id })">
          <div class="expert-stat__value">{{ upcoming.length }}</div>
          <div class="expert-stat__label">Предстоящих записей</div>
        </button>
        <button
          type="button"
          :class="['expert-stat', { 'expert-stat--attention': pending.length }]"
          @click="$emit('show-appointments', { expertId: expert.id, status: 'pending' })"
        >
          <div class="expert-stat__value">{{ pending.length }}</div>
          <div class="expert-stat__label">Ждут подтверждения</div>
        </button>
        <div class="expert-stat">
          <div class="expert-stat__value">{{ held.length }}</div>
          <div class="expert-stat__label">Проведено встреч</div>
        </div>
        <div class="expert-stat">
          <div class="expert-stat__value">{{ freeSlots.length }}</div>
          <div class="expert-stat__label">Свободных окон впереди</div>
        </div>
      </div>

      <!-- О себе -->
      <section class="expert-drawer__section">
        <div class="expert-drawer__label">Об эксперте</div>
        <div v-if="expert.short_description" class="expert-drawer__short">{{ expert.short_description }}</div>
        <div
          v-if="expert.description && expert.description !== expert.short_description"
          class="expert-drawer__description"
        >{{ expert.description }}</div>
        <div v-if="!expert.short_description && !expert.description" class="expert-drawer__muted">Описание не заполнено</div>
      </section>

      <!-- Конференция -->
      <section class="expert-drawer__section">
        <div class="expert-drawer__label">Постоянная ссылка на конференцию</div>
        <div v-if="expert.permanent_conference_url" class="d-flex align-center">
          <v-icon small color="#483CB9" class="mr-1 flex-shrink-0">mdi-video-outline</v-icon>
          <a
            :href="expert.permanent_conference_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-truncate flex-grow-1"
            :title="expert.permanent_conference_url"
          >{{ expert.permanent_conference_url }}</a>
          <v-btn icon x-small class="ml-1" title="Скопировать" @click="$emit('copy-link', expert.permanent_conference_url)">
            <v-icon small>mdi-content-copy</v-icon>
          </v-btn>
        </div>
        <div v-else class="expert-drawer__muted">
          Не задана — при подтверждении записи ссылку придётся вводить вручную.
        </div>
      </section>

      <!-- Услуги -->
      <section class="expert-drawer__section">
        <div class="expert-drawer__label">Услуги ({{ activeServicesCount }} из {{ expertServices.length }} активны)</div>
        <div v-if="!expertServices.length" class="expert-drawer__muted">Услуги не назначены</div>
        <div v-for="item in expertServices" :key="item.id" class="expert-drawer__row">
          <span :class="['text-truncate', { 'expert-drawer__muted': !item.active }]">{{ item.name }}</span>
          <span class="expert-drawer__row-meta">
            {{ item.duration }} мин
            <v-chip v-if="!item.active" x-small class="ml-2" color="#f0f0f0">{{ item.inactiveReason }}</v-chip>
          </span>
        </div>
      </section>

      <!-- Свободные окна -->
      <section class="expert-drawer__section">
        <div class="expert-drawer__label">Ближайшие свободные окна</div>
        <div v-if="!freeSlots.length" class="expert-drawer__muted">Впереди нет открытых интервалов</div>
        <button
          v-for="slot in freeSlots.slice(0, 5)"
          :key="slot.id"
          type="button"
          class="expert-drawer__item"
          @click="$emit('open-date', localDate(slot.starts_at))"
        >
          <span class="expert-drawer__item-date">{{ dateLabel(slot.starts_at) }}</span>
          <span>{{ timeRange(slot.starts_at, slot.ends_at) }}</span>
          <span v-if="(slot.unavailable_services || []).length" class="expert-drawer__item-meta">
            {{ slot.unavailable_services.length }} усл. на стопе
          </span>
        </button>
        <div v-if="freeSlots.length > 5" class="expert-drawer__more">и ещё {{ freeSlots.length - 5 }}</div>
      </section>

      <!-- Записи -->
      <section class="expert-drawer__section">
        <div class="d-flex align-center justify-space-between">
          <div class="expert-drawer__label mb-0">Ближайшие записи</div>
          <v-btn
            x-small
            text
            color="#483CB9"
            class="text-none"
            @click="$emit('show-appointments', { expertId: expert.id })"
          >
            Все записи эксперта
            <v-icon x-small right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
        <div v-if="!upcoming.length" class="expert-drawer__muted mt-1">Предстоящих записей нет</div>
        <button
          v-for="item in upcoming.slice(0, 5)"
          :key="item.id"
          type="button"
          :class="['expert-drawer__item', `expert-drawer__item--${item.status}`]"
          @click="$emit('show-appointments', { expertId: expert.id, appointmentId: item.id })"
        >
          <span class="expert-drawer__item-date">{{ dateLabel(item.scheduled_starts_at) }}</span>
          <span>{{ timeRange(item.scheduled_starts_at, item.scheduled_ends_at) }}</span>
          <span class="expert-drawer__item-main text-truncate">{{ guestName(item) }} · {{ item.service_type_name }}</span>
          <v-chip x-small :color="statusMetaOf(item).color" :text-color="statusMetaOf(item).textColor" class="expert-drawer__item-status">
            {{ statusMetaOf(item).label }}
          </v-chip>
        </button>
        <div v-if="upcoming.length > 5" class="expert-drawer__more">и ещё {{ upcoming.length - 5 }}</div>

        <div class="expert-drawer__summary">
          <div>Всего записей: <b>{{ all.length }}</b></div>
          <div v-if="cancelled.length">Отклонено и отменено: <b>{{ cancelled.length }}</b></div>
          <div v-if="lastHeld">Последняя встреча: <b>{{ dateLabel(lastHeld.scheduled_starts_at) }}</b>, {{ guestName(lastHeld) }}</div>
          <div v-if="topService">Чаще всего записываются на: <b>{{ topService }}</b></div>
        </div>
      </section>

      <!-- Ссылки -->
      <section v-if="showLinks" class="expert-drawer__section">
        <div class="d-flex align-center justify-space-between">
          <div class="expert-drawer__label mb-0">Личные ссылки на запись</div>
          <v-btn
            v-if="canManageLinks"
            x-small
            text
            color="#483CB9"
            class="text-none"
            @click="$emit('create-link', { kind: 'expert', mainId: expert.id })"
          >
            <v-icon x-small left>mdi-link-plus</v-icon>
            Ссылка
          </v-btn>
        </div>
        <div v-if="!ownLinks.length" class="expert-drawer__muted mt-1">Ссылок пока нет</div>
        <PublicLinkItem
          v-for="link in ownLinks"
          :key="`expert-${link.id}`"
          kind="expert"
          :link="link"
          :experts="experts"
          :services="services"
          :service-types="serviceTypes"
          :can-manage="canManageLinks"
          class="mt-2"
          @copy="$emit('copy-link', $event)"
          @edit="$emit('edit-link', $event)"
          @toggle-active="$emit('toggle-link-active', $event)"
        />

        <div class="expert-drawer__label mt-4">Участвует в ссылках на услуги</div>
        <div v-if="!serviceLinks.length" class="expert-drawer__muted">Не участвует</div>
        <PublicLinkItem
          v-for="link in serviceLinks"
          :key="`service-${link.id}`"
          kind="service"
          :link="link"
          :experts="experts"
          :services="services"
          :service-types="serviceTypes"
          :caption="link.name ? serviceTypeName(link.service_type) : ''"
          :show-selection="false"
          class="mt-2"
          @copy="$emit('copy-link', $event)"
        />
      </section>

      <!-- Служебное -->
      <section class="expert-drawer__section expert-drawer__meta">
        <div>Аккаунт на платформе: {{ expert.user ? 'привязан' : 'не привязан' }}</div>
        <div v-if="expert.created_at">Добавлен в календарь: {{ dateTime(expert.created_at) }}</div>
        <div v-if="expert.updated_at">Последнее изменение: {{ dateTime(expert.updated_at) }}</div>
      </section>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { utcToLocalDate, utcToLocalTime } from '@/common/calendarTime';
import PublicLinkItem from './PublicLinkItem.vue';
import {
  appointmentExpertId, expertFullName, guestFullName, initials, isAwaitingConfirmation, isCancelledStatus, statusMeta,
} from './appointmentHelpers';

const EXPERT_ROLE_LABELS = {
  manager: 'Менеджер',
  partner: 'Партнёр',
  corp_client: 'Корп. клиент',
};

export default {
  name: 'ExpertDetailsDrawer',
  components: { PublicLinkItem },
  props: {
    value: { type: Boolean, default: false },
    expert: { type: Object, default: null },
    experts: { type: Array, default: () => [] },
    services: { type: Array, default: () => [] },
    serviceTypes: { type: Array, default: () => [] },
    availability: { type: Array, default: () => [] }, // открытые интервалы
    appointments: { type: Array, default: () => [] },
    ownLinks: { type: Array, default: () => [] },
    serviceLinks: { type: Array, default: () => [] },
    showLinks: { type: Boolean, default: false },
    canManageLinks: { type: Boolean, default: false },
    isMe: { type: Boolean, default: false },
  },
  computed: {
    name() {
      return expertFullName(this.expert);
    },
    roleLabel() {
      return EXPERT_ROLE_LABELS[this.expert.role] || 'Роль не указана';
    },
    expertServices() {
      return this.services
        .filter((item) => item.expert === this.expert.id)
        .map((item) => {
          const type = this.serviceTypes.find((serviceType) => serviceType.id === item.service_type);
          const typeActive = !type || type.is_active !== false;
          const active = item.is_active !== false && typeActive;
          let inactiveReason = '';
          if (item.is_active === false) inactiveReason = 'выключена';
          else if (!typeActive) inactiveReason = 'тип услуги выключен';
          return {
            id: item.id,
            name: type ? type.name : `Услуга #${item.service_type}`,
            duration: item.max_duration_minutes,
            active,
            inactiveReason,
          };
        })
        .sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name));
    },
    activeServicesCount() {
      return this.expertServices.filter((item) => item.active).length;
    },
    freeSlots() {
      const now = this.$moment();
      return this.availability
        .filter((item) => item.expert === this.expert.id && this.$moment.utc(item.ends_at).isAfter(now))
        .sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    },
    all() {
      return this.appointments.filter((item) => appointmentExpertId(item, this.services) === this.expert.id);
    },
    cancelled() {
      return this.all.filter((item) => isCancelledStatus(item.status));
    },
    upcoming() {
      const now = this.$moment();
      return this.all
        .filter((item) => !isCancelledStatus(item.status) && this.$moment.utc(item.scheduled_ends_at).isAfter(now))
        .sort((a, b) => a.scheduled_starts_at.localeCompare(b.scheduled_starts_at));
    },
    // Только те, что ещё можно подтвердить: просроченные ожидающие не считаем.
    pending() {
      return this.all.filter(isAwaitingConfirmation);
    },
    // Проведённые — подтверждённые встречи, время которых уже прошло.
    held() {
      const now = this.$moment();
      return this.all
        .filter((item) => item.status === 'confirmed' && this.$moment.utc(item.scheduled_ends_at).isBefore(now))
        .sort((a, b) => b.scheduled_starts_at.localeCompare(a.scheduled_starts_at));
    },
    lastHeld() {
      return this.held[0] || null;
    },
    topService() {
      const counts = {};
      this.all.filter((item) => !isCancelledStatus(item.status)).forEach((item) => {
        if (item.service_type_name) counts[item.service_type_name] = (counts[item.service_type_name] || 0) + 1;
      });
      const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
      return entries.length > 1 ? entries[0][0] : '';
    },
  },
  methods: {
    initialsOf(name) {
      return initials(name);
    },
    guestName(item) {
      return guestFullName(item) || item.guest_email || 'Клиент';
    },
    statusMetaOf(item) {
      return statusMeta(item.status);
    },
    serviceTypeName(id) {
      const type = this.serviceTypes.find((item) => item.id === id);
      return type ? type.name : '';
    },
    localDate(iso) {
      return utcToLocalDate(iso);
    },
    timeRange(start, end) {
      return `${utcToLocalTime(start)} – ${utcToLocalTime(end)}`;
    },
    dateLabel(iso) {
      const day = this.$moment.utc(iso).local().locale('ru');
      const diff = day.clone().startOf('day').diff(this.$moment().startOf('day'), 'days');
      if (diff === 0) return 'Сегодня';
      if (diff === 1) return 'Завтра';
      return day.format(day.year() === this.$moment().year() ? 'dd, D MMM' : 'D MMM YYYY');
    },
    dateTime(iso) {
      return this.$moment.utc(iso).local().locale('ru').format('D MMMM YYYY, HH:mm');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_tokens.scss';

.min-width-0 {
  min-width: 0;
}

.expert-drawer__body {
  padding: $space-5;
  font-size: $text-sm;
}

.expert-drawer__name {
  font-size: 18px;
  font-weight: $weight-semibold;
  line-height: 1.3;
}

.expert-drawer__email {
  font-size: $text-sm;
  color: #483CB9;
}

.expert-drawer__stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: $space-2;
  margin-top: $space-4;
}

.expert-stat {
  text-align: left;
  padding: $space-3;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: $radius-sm;

  &__value {
    font-size: 20px;
    font-weight: 700;
    line-height: 1.1;
  }

  &__label {
    font-size: $text-xs;
    color: #8a8f9c;
  }

  &--attention &__value {
    color: #B26A00;
  }
}

button.expert-stat {
  transition: background-color $transition-fast;

  &:hover {
    background: #f7f6fd;
  }
}

.expert-drawer__section {
  margin-top: $space-5;
}

.expert-drawer__label {
  font-size: 11px;
  font-weight: $weight-semibold;
  color: #8a8f9c;
  text-transform: uppercase;
  margin-bottom: $space-2;
}

.expert-drawer__short {
  font-weight: $weight-medium;
  margin-bottom: $space-1;
}

.expert-drawer__description {
  white-space: pre-line;
  color: #5f6472;
  line-height: 1.45;
}

.expert-drawer__muted {
  color: #8a8f9c;
}

.expert-drawer__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $space-3;
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.expert-drawer__row-meta {
  flex-shrink: 0;
  color: #5f6472;
}

.expert-drawer__item {
  display: grid;
  grid-template-columns: 96px 1fr auto;
  align-items: center;
  column-gap: $space-2;
  width: 100%;
  text-align: left;
  padding: 6px $space-2;
  margin-top: 4px;
  border-radius: $radius-xs;
  border-left: 3px solid #483CB9;
  background: #fafafc;
  transition: background-color $transition-fast;

  &:hover {
    background: #f1effc;
  }

  &--pending { border-left-color: #F0A030; }
  &--confirmed { border-left-color: #009E60; }
}

.expert-drawer__item-date {
  font-weight: $weight-medium;
}

.expert-drawer__item-meta {
  font-size: $text-xs;
  color: #8a8f9c;
}

.expert-drawer__item-main {
  grid-column: 2 / 3;
  font-size: $text-xs;
  color: #5f6472;
  min-width: 0;
}

.expert-drawer__item-status {
  grid-column: 3;
  grid-row: 1 / span 2;
}

.expert-drawer__more {
  font-size: $text-xs;
  color: #8a8f9c;
  margin-top: 4px;
}

.expert-drawer__summary {
  margin-top: $space-3;
  padding: $space-3;
  border-radius: $radius-sm;
  background: #f7f7f9;
  line-height: 1.7;
}

.expert-drawer__meta {
  padding-top: $space-3;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  font-size: $text-xs;
  color: #8a8f9c;
  line-height: 1.7;
}
</style>
